import { frequencyMessages } from "$routes/_lib/prescription-strings";
import { FormattedMessage, useIntl } from "react-intl";

export const dosageCount = (rateQuantity, durationQuantity, doseQuantity?) => {
  let freq = rateQuantity?.value || "";
  const freqUnit = rateQuantity?.unit || false;
  let duration = durationQuantity?.value || "";
  const durationUnit = durationQuantity?.unit || false;
  const quantity = doseQuantity?.value || false;
  const shouldCalcWithDose =
    doseQuantity?.unit === "tablet" || doseQuantity?.unit === "capsule";

  if (freqUnit && durationUnit) {
    switch (freqUnit.toLowerCase()) {
      case "week":
        freq /= 7;
        break;
      case "month":
        freq /= 28;
        break;
      default:
        break;
    }

    switch (durationUnit.toLowerCase()) {
      case "week":
        duration *= 7;
        break;
      case "month":
        duration *= 28;
        break;
      default:
        break;
    }
  }

  if (quantity && shouldCalcWithDose) {
    return Math.ceil(Number(freq) * Number(duration) * quantity);
  }

  return Math.ceil(Number(freq) * Number(duration));
};

export type StylableDoseProps = {
  asNeeded?: boolean;
  isStat?: boolean;
  frequency?: {
    value?: number;
    unit?: string;
  };
  duration?: {
    value?: number;
    unit?: string;
  };
};

const StyledDosageString: FC<StylableDoseProps> = ({
  asNeeded = false,
  isStat = false,
  frequency = {},
  duration = {},
}) => {
  const { formatMessage } = useIntl();

  const { value: frequencyValue, unit: frequencyUnit } = frequency ?? {};
  const { value: durationValue, unit: durationUnit } = duration ?? {};

  const lowercaseFrequencyUnit = frequencyUnit?.toLowerCase();

  const translatedFrequencyUnit = frequencyMessages[lowercaseFrequencyUnit]
    ? formatMessage(frequencyMessages[lowercaseFrequencyUnit], { value: 1 }) // drives "3 times per day" in here we never want "day" to be plural
    : frequencyUnit;

  const translatedDurationUnit = frequencyMessages[durationUnit?.toLowerCase()]
    ? formatMessage(frequencyMessages[durationUnit.toLowerCase()], {
        value: durationValue,
      })
    : durationUnit;

  if (isStat) {
    return (
      <FormattedMessage
        id="prescription.dosageString.isStat"
        defaultMessage="Once"
      />
    );
  }

  if (asNeeded && frequencyValue > 0 && durationValue > 0) {
    return (
      <FormattedMessage
        id="prescription.dosageString.asRequiredWithFrequencyAndDuration"
        description="Dosage string for prescriptions e.g. 1 time per week for 3 months as required (the frequencyUnit / durationUnit are translated elsewhere but will be day, week or month)"
        defaultMessage="{frequencyValue} {frequencyValue, plural, one {time} other {times}} per {frequencyUnit} for {durationValue} {durationUnit} as required"
        values={{
          b: (chunks) => <span>{chunks}</span>,
          frequencyValue,
          frequencyUnit: translatedFrequencyUnit,
          durationValue,
          durationUnit: translatedDurationUnit,
        }}
      />
    );
  }

  if (frequencyValue > 0 && durationValue > 0) {
    return (
      <FormattedMessage
        id="prescription.dosageString.withFrequencyAndDuration"
        description="Dosage string for prescriptions e.g. 1 time per week for 3 months (the frequencyUnit / durationUnit are translated elsewhere but will be day, week or month)"
        defaultMessage="{frequencyValue} {frequencyValue, plural, one {time} other {times}} per {frequencyUnit} for {durationValue} {durationUnit}"
        values={{
          b: (chunks) => <span>{chunks}</span>,
          frequencyValue,
          frequencyUnit: translatedFrequencyUnit,
          durationValue,
          durationUnit: translatedDurationUnit,
        }}
      />
    );
  }

  if (frequencyValue > 0) {
    return (
      <FormattedMessage
        id="prescription.dosageString.withFrequency"
        description="Dosage string for prescriptions e.g. 1 time per week (the frequencyUnit is translated elsewhere but will be day, week or month)"
        defaultMessage="{frequencyValue} {frequencyValue, plural, one {time} other {times}} per {frequencyUnit}"
        values={{
          b: (chunks) => <span>{chunks}</span>,
          frequencyValue,
          frequencyUnit: translatedFrequencyUnit,
        }}
      />
    );
  }

  if (asNeeded) {
    return (
      <FormattedMessage
        id="prescription.dosageString.asRequired"
        defaultMessage="As required"
      />
    );
  }
  return (
    <FormattedMessage
      id="prescription.dose.errorMessage"
      defaultMessage="Error getting dosage information"
    />
  );
};

export default StyledDosageString;
